import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Tab, Tabs } from 'react-bootstrap';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaCog, FaSave } from 'react-icons/fa';
import Loading from '~/Components/Loading';
import {
  buscarParametrosFusao,
  salvarParametrosFusaoSetCorp,
  salvarParametrosFusaoSetPar,
} from '~/Services/Service';

const schemaFusao = Yup.object().shape({
  localDigitalizacao: Yup.string()
    .required('Informe o caminho de digitalização')
    .max(60, 'Máximo 60 caracteres'),
  localImgPj: Yup.string()
    .required('Informe o caminho de imagens PJ')
    .max(150, 'Máximo 150 caracteres'),
  localParceriaWeb: Yup.string()
    .required('Informe o caminho de parceria web')
    .max(100, 'Máximo 100 caracteres'),
});

const mapearEntidadeParaForm = entidade => ({
  localDigitalizacao:
    entidade && entidade.local_digitalizacao ? entidade.local_digitalizacao : '',
  localImgPj: entidade && entidade.local_img_pj ? entidade.local_img_pj : '',
  localParceriaWeb:
    entidade && entidade.local_parceria_web ? entidade.local_parceria_web : '',
});

const mapearFormParaPayload = values => ({
  LocalDigitalizacao: values.localDigitalizacao,
  LocalImgPj: values.localImgPj,
  LocalParceriaWeb: values.localParceriaWeb,
});

const FormularioFusao = ({ qual, entidade, salvando, onSubmit }) => (
  <Formik
    initialValues={mapearEntidadeParaForm(entidade)}
    validationSchema={schemaFusao}
    onSubmit={onSubmit}
    enableReinitialize={false}
  >
    {({ isValid, dirty, isSubmitting }) => (
      <FormikForm>
        <Row>
          <Col xs={12}>
            <div className="form-group mb-3">
              <label
                className="small mb-1"
                style={{ fontWeight: 500 }}
                htmlFor={`${qual}-localDigitalizacao`}
              >
                Local de Digitalização
              </label>
              <Field
                id={`${qual}-localDigitalizacao`}
                name="localDigitalizacao"
                type="text"
                className="form-control form-control-sm"
                maxLength={60}
                placeholder="Ex.: C:\\Setpar\\Digitalizacao"
              />
              <ErrorMessage
                name="localDigitalizacao"
                component="div"
                className="text-danger small mt-1"
              />
              <small className="text-muted">Máximo de 60 caracteres.</small>
            </div>
          </Col>
          <Col xs={12}>
            <div className="form-group mb-3">
              <label
                className="small mb-1"
                style={{ fontWeight: 500 }}
                htmlFor={`${qual}-localImgPj`}
              >
                Local de Imagens PJ
              </label>
              <Field
                id={`${qual}-localImgPj`}
                name="localImgPj"
                type="text"
                className="form-control form-control-sm"
                maxLength={150}
                placeholder="Ex.: C:\\Setpar\\Imagens\\PJ"
              />
              <ErrorMessage
                name="localImgPj"
                component="div"
                className="text-danger small mt-1"
              />
              <small className="text-muted">Máximo de 150 caracteres.</small>
            </div>
          </Col>
          <Col xs={12}>
            <div className="form-group mb-3">
              <label
                className="small mb-1"
                style={{ fontWeight: 500 }}
                htmlFor={`${qual}-localParceriaWeb`}
              >
                Local de Parceria Web
              </label>
              <Field
                id={`${qual}-localParceriaWeb`}
                name="localParceriaWeb"
                type="text"
                className="form-control form-control-sm"
                maxLength={100}
                placeholder="Ex.: C:\\Setpar\\ParceriaWeb"
              />
              <ErrorMessage
                name="localParceriaWeb"
                component="div"
                className="text-danger small mt-1"
              />
              <small className="text-muted">Máximo de 100 caracteres.</small>
            </div>
          </Col>
        </Row>
        <div className="d-flex justify-content-end align-items-center mt-2">
          {dirty && !isSubmitting && (
            <small className="text-muted mr-3">Alterações não salvas</small>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid || isSubmitting || salvando}
            className="d-flex align-items-center"
          >
            <FaSave className="mr-2" />
            {isSubmitting || salvando ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </div>
      </FormikForm>
    )}
  </Formik>
);

const ParametrosFusao = () => {
  const [dados, setDados] = useState({ setcorp: null, setpar: null });
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState({
    setcorp: false,
    setpar: false,
  });

  useEffect(() => {
    let ativo = true; // LICOES Ciclo 9 L3 — guard contra resposta stale
    buscarParametrosFusao()
      .then(data => {
        if (!ativo) return;
        if (!data || !data.set_corp || !data.set_par) {
          throw new Error('Resposta incompleta do servidor.');
        }
        setDados({ setcorp: data.set_corp, setpar: data.set_par });
      })
      .catch(err => {
        if (!ativo) return;
        const mensagem =
          err && err.message ? err.message : 'Erro ao carregar parâmetros.';
        toast.error(mensagem);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const handleSubmit = qual => async (values, { setSubmitting }) => {
    setSalvando(s => ({ ...s, [qual]: true }));
    const payload = mapearFormParaPayload(values);
    const fn =
      qual === 'setcorp'
        ? salvarParametrosFusaoSetCorp
        : salvarParametrosFusaoSetPar;
    try {
      await fn(payload);
      toast.success('Parâmetros atualizados com sucesso.');
      setDados(d => ({
        ...d,
        [qual]: {
          local_digitalizacao: values.localDigitalizacao,
          local_img_pj: values.localImgPj,
          local_parceria_web: values.localParceriaWeb,
        },
      }));
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao salvar parâmetros.';
      toast.error(mensagem);
    } finally {
      setSalvando(s => ({ ...s, [qual]: false }));
      setSubmitting(false);
    }
  };

  if (carregando) {
    return <Loading />;
  }

  if (!dados.setcorp || !dados.setpar) {
    return (
      <Container fluid className="px-3 py-3">
        <div className="alert alert-danger" role="alert">
          Não foi possível carregar os parâmetros. Tente novamente em instantes.
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3 py-3">
      <Row
        className="py-3 mb-3 mx-auto justify-content-between align-items-center"
        style={{
          backgroundColor: '#0747a6',
          borderRadius: '8px',
          color: '#fff',
        }}
      >
        <Col className="d-flex align-items-center px-3">
          <FaCog className="mr-2" />
          <h4 className="mb-0">Parâmetros do Sistema — Fusão</h4>
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={10} xl={8} className="mx-auto">
          <Card
            className="border-0"
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <Card.Body>
              <Tabs
                defaultActiveKey="setcorp"
                id="parametros-fusao-tabs"
                className="mb-3"
                mountOnEnter={false}
                unmountOnExit={false}
              >
                <Tab eventKey="setcorp" title="SetCorp">
                  <FormularioFusao
                    qual="setcorp"
                    entidade={dados.setcorp}
                    salvando={salvando.setcorp}
                    onSubmit={handleSubmit('setcorp')}
                  />
                </Tab>
                <Tab eventKey="setpar" title="SetPar">
                  <FormularioFusao
                    qual="setpar"
                    entidade={dados.setpar}
                    salvando={salvando.setpar}
                    onSubmit={handleSubmit('setpar')}
                  />
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ParametrosFusao;
